import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {assert} from 'Asserts';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsDashboardSelectorQuery} from 'insights/analytics-dashboards/__generated/AnalyticsDashboardSelectorQuery';
import AnalyticsDashboardSelector from 'insights/analytics-dashboards/AnalyticsDashboardSelector';
import {convertGQLToRESTParameterValueTemplate} from 'insights/embeddings/data/ParameterValueTemplateGQLRESTConverters';
import FormGroup from 'ui-library/bs/FormGroup';
import {ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Modal from 'ui-library/modals/Modal';

import {AnalyticsParameterValueTemplateGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterValueTemplateGraphQL';
import {EmbeddableParameterType} from '../EmbeddingConstants';
import {RESTAnalyticsParameterValueTemplate} from '../RESTEmbeddingTypes';
import {ParameterValueTemplateFormQuery} from './__generated/ParameterValueTemplateFormQuery';
import ParameterValueTemplateForm from './ParameterValueTemplateForm';
import useParameterValueTemplateFormState from './useParameterValueTemplateFormState';

type AnalyticsParameterConfig = ExtractByTypename<
  ParameterValueTemplateFormQuery['analyticsDashboard'],
  'AnalyticsDashboardNode'
>['parameterConfigs'][number];

type AnalyticsDashboard =
  AnalyticsDashboardSelectorQuery['analyticsDashboardListing']['analyticsDashboards'][number];

interface DashboardLinkConfigModalOutput {
  dashboard: AnalyticsDashboard;
  parameterValueTemplates: Array<RESTAnalyticsParameterValueTemplate>;
}

export interface DashboardLinkConfigModalProps<T extends EmbeddableParameterType> {
  embeddingType: T;
  title: string;
  embeddedDashboard: AnalyticsDashboard | null;
  valueTemplates: Array<AnalyticsParameterValueTemplateGraphQL>;
  focusConfig?: Pick<AnalyticsParameterConfig, 'id'>;
  onRequestClose: () => void;
  onRequestSubmit: (output: DashboardLinkConfigModalOutput) => void;
}

export default function DashboardLinkConfigModal<T extends EmbeddableParameterType>(
  props: DashboardLinkConfigModalProps<T>
): JSX.Element {
  const [selectedDashboard, setSelectedDashboard] = useState<AnalyticsDashboard | null>(
    props.embeddedDashboard
  );
  const parameterTemplateFormState = useParameterValueTemplateFormState(props.embeddingType);

  return (
    <Modal
      title={props.title}
      buttons={[
        {
          text: 'Save',
          style: ButtonStyle.PRIMARY,
          submit: true,
          disabled: selectedDashboard == null || parameterTemplateFormState.hasInvalidInputs,
        },
      ]}
      onRequestSubmit={() => {
        // Save button is disabled if selectedDashboard == null, so if we get here, it should never be null
        assert(selectedDashboard != null, 'Attempted to submit modal with no selected dashboard');
        props.onRequestSubmit({
          dashboard: selectedDashboard,
          parameterValueTemplates: parameterTemplateFormState.parameterValueTemplates.map((valueTemplate) =>
            convertGQLToRESTParameterValueTemplate(valueTemplate)
          ),
        });
      }}
      onRequestClose={() => {
        props.onRequestClose();
      }}
    >
      <FormGroup>
        <FieldLabel label="Dashboard" />
        <AnalyticsDashboardSelector
          onSelect={(newSelectedDashboard) => {
            setSelectedDashboard(newSelectedDashboard);
          }}
          selectedAnalyticsDashboard={selectedDashboard}
        />
      </FormGroup>
      {selectedDashboard && (
        <FormGroup data-test="dashboard-link-config-modal-parameter-section">
          <FieldLabel label="Parameters" />
          <ParameterValueTemplateForm
            embeddingType={props.embeddingType}
            selectedDashboard={selectedDashboard}
            parameterTemplateFormState={parameterTemplateFormState}
            initialValueTemplateData={
              props.embeddedDashboard && {
                dashboardId: props.embeddedDashboard.id,
                valueTemplates: props.valueTemplates,
              }
            }
            focusConfig={props.focusConfig}
          />
        </FormGroup>
      )}
      <div className={classNames(css(styles.warningWrapper), 'u-vertical-center-items')}>
        <BenchlingIcon icon="info-circle" className="u-mini-spacing-right" />
        Entities with the embedded dashboard must be refreshed for changes to take effect
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  warningWrapper: {
    color: GRAY_DARKER,
  },
});
